import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, CircularProgress, Alert, Chip,
  Checkbox, FormControlLabel, TextField, Paper, Divider,
  List, ListItem, ListItemText, ListItemIcon,
  InputAdornment, IconButton, Tooltip, Select, MenuItem,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ClearIcon from '@mui/icons-material/Clear';
import MapIcon from '@mui/icons-material/Map';
import NearMeIcon from '@mui/icons-material/NearMe';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import apiClient from '../api/client';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({ iconUrl: markerIcon, iconRetinaUrl: markerIcon2x, shadowUrl: markerShadow });

const numberedIcon = (n, color = '#1976d2') => L.divIcon({
  className: '',
  html: `<div style="background:${color};color:white;border-radius:50%;width:30px;height:30px;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:13px;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.4)">${n}</div>`,
  iconSize: [30, 30],
  iconAnchor: [15, 15],
});

const startIcon = L.divIcon({
  className: '',
  html: `<div style="background:#2e7d32;color:white;border-radius:50%;width:30px;height:30px;display:flex;align-items:center;justify-content:center;font-size:16px;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.4)">🏠</div>`,
  iconSize: [30, 30],
  iconAnchor: [15, 15],
});

const fmt = (iso) => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const defaultStartTime = () => {
  const d = new Date();
  d.setDate(d.getDate() + (d.getDay() === 0 ? 1 : 0)); // skip to monday if sunday
  d.setHours(9, 0, 0, 0);
  return d.toISOString().slice(0, 16);
};

const geocode = async (address) => {
  const res = await fetch(
    `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(address)}&format=json&limit=1`,
    { headers: { 'Accept-Language': 'en' } }
  );
  const data = await res.json();
  if (!data.length) throw new Error(`Could not find location: "${address}"`);
  return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
};

const DayPlanner = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const clientIdParam = searchParams.get('client');

  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState(clientIdParam || '');
  const [clientData, setClientData] = useState(null);
  const [properties, setProperties] = useState([]);
  const [loadingProps, setLoadingProps] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [startAddress, setStartAddress] = useState('');
  const [startTime, setStartTime] = useState(defaultStartTime);
  const [defaultDuration, setDefaultDuration] = useState(30);
  const [overrides, setOverrides] = useState({});
  const [anchors, setAnchors] = useState({});
  const [mustShows, setMustShows] = useState(new Set());
  const [completeBy, setCompleteBy] = useState('');
  const [result, setResult] = useState(null);
  const [planning, setPlanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedPlan, setSavedPlan] = useState(null);
  const [adHocName, setAdHocName] = useState('');

  // Load clients and all properties
  useEffect(() => {
    Promise.all([
      apiClient.get('/clients'),
      apiClient.get('/properties'),
    ]).then(([cr, pr]) => {
      setClients(cr.data.data);
      setProperties(pr.data.data);
    }).finally(() => setLoadingProps(false));
  }, []);

  // When client changes, pre-load their assigned properties + preferences
  const loadClientPreferences = useCallback((cId, allProps) => {
    if (!cId) { setClientData(null); return; }
    apiClient.get(`/clients/${cId}`).then(r => {
      const cd = r.data.data;
      setClientData(cd);
      const cps = cd.clientProperties ?? [];
      // Auto-select client's unvisited properties
      setSelected(new Set(cps.filter(cp => !cp.visited).map(cp => cp.property_id)));
      // Pre-load must_show flags
      setMustShows(new Set(cps.filter(cp => cp.must_show).map(cp => cp.property_id)));
      // Pre-load anchor times
      const anchorMap = {};
      const overrideMap = {};
      cps.forEach(cp => {
        if (cp.anchor_time) anchorMap[cp.property_id] = cp.anchor_time;
        if (cp.duration_override) overrideMap[cp.property_id] = cp.duration_override;
      });
      setAnchors(anchorMap);
      setOverrides(overrideMap);
    });
  }, []);

  useEffect(() => {
    if (clientId && properties.length) loadClientPreferences(clientId, properties);
  }, [clientId, properties, loadClientPreferences]);

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const setOverride = (id, val) => setOverrides(prev => ({ ...prev, [id]: val === '' ? undefined : Number(val) }));
  const setAnchor = (id, val) => setAnchors(prev => ({ ...prev, [id]: val || undefined }));
  const toggleMustShow = (id) => setMustShows(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const buildPayload = (startLocation, save = false) => ({
    client_id: clientId || null,
    plan_name: clientId ? null : (adHocName.trim() || null),
    start_location: { ...startLocation, address: startAddress },
    start_time: new Date(startTime).toISOString(),
    default_duration_minutes: defaultDuration,
    property_ids: [...selected],
    duration_overrides: Object.fromEntries(Object.entries(overrides).filter(([, v]) => v != null)),
    anchor_times: Object.fromEntries(
      Object.entries(anchors).filter(([, v]) => v).map(([id, timeStr]) => {
        const date = startTime.slice(0, 10);
        return [id, new Date(`${date}T${timeStr}`).toISOString()];
      })
    ),
    must_show_ids: [...mustShows],
    complete_by: completeBy ? new Date(`${startTime.slice(0, 10)}T${completeBy}`).toISOString() : null,
    save,
  });

  const handlePlan = async () => {
    if (selected.size === 0) return;
    if (!startAddress.trim()) { setError('Enter a starting address'); return; }
    setPlanning(true);
    setError(null);
    setResult(null);
    setSavedPlan(null);
    try {
      const startLocation = await geocode(startAddress);
      const res = await apiClient.post('/schedule/plan', buildPayload(startLocation, false));
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setPlanning(false);
    }
  };

  const handleSave = async () => {
    if (!result) return;
    setSaving(true);
    try {
      const startLocation = { lat: result.start_location.lat, lng: result.start_location.lng };
      const res = await apiClient.post('/schedule/plan', buildPayload(startLocation, true));
      setSavedPlan(res.data.saved_plan);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  // When a client is selected, only show their assigned properties; otherwise show all
  const assignedPropertyIds = clientData
    ? new Set(clientData.clientProperties?.map(cp => cp.property_id) ?? [])
    : null;
  const visibleProperties = assignedPropertyIds
    ? properties.filter(p => assignedPropertyIds.has(p.id))
    : properties;

  const selectedProps = properties.filter(p => selected.has(p.id));
  const mappableSelected = selectedProps.filter(p => p.latitude != null && p.longitude != null);
  const unmappable = selectedProps.filter(p => p.latitude == null || p.longitude == null);

  const buildMapsUrl = (fromStopIndex) => {
    // fromStopIndex = -1 means start from the origin address
    const stops = result.route;
    const originIsStart = fromStopIndex === -1;
    const origin = originIsStart
      ? `${result.start_location.lat},${result.start_location.lng}`
      : `${stops[fromStopIndex].property.latitude},${stops[fromStopIndex].property.longitude}`;
    const remaining = originIsStart ? stops : stops.slice(fromStopIndex + 1);
    const destination = `${result.start_location.lat},${result.start_location.lng}`;
    const waypointList = remaining.map(s => `${s.property.latitude},${s.property.longitude}`);
    // Google Maps URL caps at 10 waypoints (stops between origin and destination)
    const waypoints = waypointList.slice(0, 10).join('|');
    const params = new URLSearchParams({ api: '1', origin, destination, travelmode: 'driving' });
    if (waypoints) params.set('waypoints', waypoints);
    return `https://www.google.com/maps/dir/?${params.toString()}`;
  };

  const routePositions = result?.route.map(s => [parseFloat(s.property.latitude), parseFloat(s.property.longitude)]);
  const startPos = result ? [result.start_location.lat, result.start_location.lng] : null;
  const allPositions = startPos && routePositions ? [startPos, ...routePositions, startPos] : null;
  const roadGeometry = result?.route_geometry?.map(p => [p.lat, p.lng]) ?? null;
  const mapCenter = allPositions ? allPositions[0] : [39.5, -98.35];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Plan a Showing Day</Typography>

      {clientId && (
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/clients/${clientId}`)} sx={{ mb: 2 }}>
          Back to {clientData?.first_name} {clientData?.last_name}
        </Button>
      )}

      <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: 'wrap' }}>

        {/* Left panel: selector + form */}
        <Paper sx={{ p: 3, flex: '0 0 360px', minWidth: 300 }}>

          <Typography variant="h6" gutterBottom>Client (optional)</Typography>
          <Select
            fullWidth
            size="small"
            value={clientId}
            onChange={e => { setClientId(e.target.value); setSelected(new Set()); setMustShows(new Set()); setAnchors({}); setOverrides({}); }}
            displayEmpty
            sx={{ mb: 2 }}
          >
            <MenuItem value="">No client — ad hoc plan</MenuItem>
            {clients.map(c => <MenuItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</MenuItem>)}
          </Select>
          {clientData && (
            <Alert severity="info" sx={{ mb: 2, py: 0, fontSize: 12 }}>
              Pre-loaded {clientData.clientProperties?.filter(cp => !cp.visited).length} unvisited properties with saved preferences.
            </Alert>
          )}

          <Divider sx={{ mb: 2 }} />

          <Typography variant="h6" gutterBottom>1. Select Properties</Typography>
          {loadingProps ? <CircularProgress size={20} /> : (
            <Box sx={{ maxHeight: 240, overflowY: 'auto', mb: 2 }}>
              {visibleProperties.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  {clientData ? 'No properties assigned to this client.' : 'No properties yet.'}
                </Typography>
              )}
              {visibleProperties.map(p => (
                <Box key={p.id} sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    control={<Checkbox checked={selected.has(p.id)} onChange={() => toggle(p.id)} size="small" />}
                    label={
                      <Box>
                        <Typography variant="body2">{p.address}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {p.city}, {p.state}
                          {(p.latitude == null || p.longitude == null) && ' · ⚠ no coords'}
                        </Typography>
                      </Box>
                    }
                    sx={{ flex: 1, mr: 0 }}
                  />
                  {selected.has(p.id) && (
                    <Tooltip title={mustShows.has(p.id) ? 'Must show (click to unset)' : 'Mark as must-show'}>
                      <IconButton size="small" onClick={() => toggleMustShow(p.id)} sx={{ color: mustShows.has(p.id) ? 'warning.main' : 'action.disabled' }}>
                        {mustShows.has(p.id) ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              ))}
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          <Typography variant="h6" gutterBottom>2. Starting Point</Typography>
          <TextField
            label="Starting address"
            placeholder="e.g. 1 Congress Ave, Austin, TX"
            value={startAddress}
            onChange={e => setStartAddress(e.target.value)}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          />
          <TextField
            label="Date & time"
            type="datetime-local"
            value={startTime}
            onChange={e => setStartTime(e.target.value)}
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Complete by (optional)"
            type="time"
            value={completeBy}
            onChange={e => setCompleteBy(e.target.value)}
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
            helperText="Optional properties dropped if needed to meet this time"
            sx={{ mb: 2 }}
          />

          <Divider sx={{ my: 2 }} />

          <Typography variant="h6" gutterBottom>3. Showing Durations</Typography>
          <TextField
            label="Default duration"
            type="number"
            value={defaultDuration}
            onChange={e => setDefaultDuration(Number(e.target.value))}
            size="small"
            InputProps={{ endAdornment: <InputAdornment position="end">min</InputAdornment> }}
            sx={{ mb: 2, width: 160 }}
          />

          {selectedProps.length > 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Override per property (leave blank to use default):
              </Typography>
              {selectedProps.map(p => (
                <Box key={p.id} sx={{ mb: 1.5 }}>
                  <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 500, mb: 0.5 }} noWrap>
                    {p.address}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      type="number"
                      size="small"
                      placeholder={String(defaultDuration)}
                      value={overrides[p.id] ?? ''}
                      onChange={e => setOverride(p.id, e.target.value)}
                      InputProps={{ endAdornment: <InputAdornment position="end">min</InputAdornment> }}
                      sx={{ width: 110 }}
                    />
                    <TextField
                      type="time"
                      size="small"
                      label="Anchor"
                      value={anchors[p.id] ?? ''}
                      onChange={e => setAnchor(p.id, e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        endAdornment: anchors[p.id] ? (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => setAnchor(p.id, '')} edge="end">
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ) : null,
                      }}
                      sx={{ width: 150 }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {unmappable.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2, fontSize: 12 }}>
              {unmappable.length} selected {unmappable.length === 1 ? 'property has' : 'properties have'} no
              coordinates and will be skipped.
            </Alert>
          )}

          <Button
            variant="contained"
            fullWidth
            sx={{ mt: 3 }}
            onClick={handlePlan}
            disabled={planning || selected.size === 0 || mappableSelected.length === 0}
          >
            {planning ? <><CircularProgress size={16} sx={{ mr: 1 }} />Optimizing…</> : 'Optimize Route'}
          </Button>

          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </Paper>

        {/* Right panel: results */}
        <Box sx={{ flex: 1, minWidth: 300 }}>
          {!result && !planning && (
            <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              <Typography>Select properties and a starting point, then click Optimize Route.</Typography>
            </Paper>
          )}

          {result && (
            <>
              {/* Dropped properties */}
              {result.dropped?.length > 0 && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  <strong>{result.dropped.length} {result.dropped.length === 1 ? 'property' : 'properties'} dropped to meet deadline:</strong>{' '}
                  {result.dropped.map(p => p.address).join(', ')}
                </Alert>
              )}

              {/* Feasibility warnings */}
              {result.warnings?.map((w, i) => (
                <Alert severity="warning" key={i} sx={{ mb: 1 }}>
                  <strong>{w.address}</strong> — {w.message}
                </Alert>
              ))}

              {/* Save plan */}
              {!savedPlan && (
                <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
                  {!clientId && (
                    <TextField
                      size="small"
                      placeholder="Plan name (optional)"
                      value={adHocName}
                      onChange={e => setAdHocName(e.target.value)}
                      sx={{ flexGrow: 1, input: { color: '#f1f5f9' }, '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#334155' } } }}
                    />
                  )}
                  <Button variant="outlined" startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />} onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving…' : 'Save Plan'}
                  </Button>
                </Box>
              )}
              {savedPlan && (
                <Alert severity="success" sx={{ mb: 2 }} action={
                  clientId ? <Button size="small" onClick={() => navigate(`/clients/${clientId}`)}>View</Button> : null
                }>
                  Saved as "{savedPlan.name}"
                </Alert>
              )}

              {/* Send to Google Maps */}
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<MapIcon />}
                  onClick={() => window.open(buildMapsUrl(-1), '_blank')}
                  size="small"
                >
                  Send to Google Maps
                </Button>
              </Box>

              {/* Summary chips */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                <Chip label={`${result.route.length} stops`} color="primary" />
                <Chip label={`${result.summary.total_travel_minutes} min driving`} />
                <Chip label={`${result.summary.total_showing_minutes} min showing`} />
                <Chip label={`Done by ${fmt(result.summary.end_time)}`} color="success" />
                {result.routing_source === 'haversine' && (
                  <Chip label="⚠ straight-line estimate" color="warning" size="small" />
                )}
                {result.skipped_count > 0 && (
                  <Chip label={`${result.skipped_count} skipped (no coords)`} color="warning" size="small" />
                )}
              </Box>

              {/* Route map */}
              {allPositions && (
                <Paper sx={{ mb: 2, overflow: 'hidden', borderRadius: 2 }}>
                  <MapContainer center={mapCenter} zoom={13} style={{ height: 320 }}>
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    {roadGeometry
                      ? <Polyline positions={roadGeometry} color="#1976d2" weight={4} />
                      : <Polyline positions={allPositions} color="#1976d2" weight={3} dashArray="6,4" />
                    }
                    <Marker position={startPos} icon={startIcon}>
                      <Popup><strong>Start</strong><br />{result.start_location.address}</Popup>
                    </Marker>
                    {result.route.map((stop, i) => (
                      <Marker
                        key={stop.property.id}
                        position={[parseFloat(stop.property.latitude), parseFloat(stop.property.longitude)]}
                        icon={numberedIcon(i + 1)}
                      >
                        <Popup>
                          <strong>{i + 1}. {stop.property.address}</strong><br />
                          Arrive {fmt(stop.arrival_time)} · {stop.duration_minutes} min showing
                        </Popup>
                      </Marker>
                    ))}
                  </MapContainer>
                </Paper>
              )}

              {/* Stop timeline */}
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Route</Typography>
                <List dense disablePadding>
                  {/* Start */}
                  <ListItem disablePadding sx={{ mb: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Box sx={{ width: 28, height: 28, borderRadius: '50%', background: '#2e7d32', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>🏠</Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={result.start_location.address}
                      secondary={`Depart ${fmt(new Date(startTime).toISOString())}`}
                    />
                    <Tooltip title="Start full route from here">
                      <IconButton size="small" onClick={() => window.open(buildMapsUrl(-1), '_blank')} sx={{ color: 'text.secondary' }}>
                        <MapIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </ListItem>

                  {result.route.map((stop, i) => (
                    <React.Fragment key={stop.property.id}>
                      {/* Travel leg */}
                      <ListItem disablePadding sx={{ pl: '20px', py: 0 }}>
                        <Typography variant="caption" color="text.secondary">
                          ↓ {stop.travel_time_minutes} min · {stop.distance_km} km
                        </Typography>
                      </ListItem>
                      {/* Stop */}
                      <ListItem disablePadding sx={{ mb: 1 }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <Box sx={{ width: 28, height: 28, borderRadius: '50%', background: '#1976d2', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 'bold', fontSize: 13 }}>{i + 1}</Box>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                              {stop.property.address}, {stop.property.city}
                              {stop.must_show && <Chip label="must show" size="small" color="warning" sx={{ height: 18, fontSize: 10 }} />}
                              {stop.anchored && <Chip label="anchored" size="small" color="secondary" sx={{ height: 18, fontSize: 10 }} />}
                              {stop.property.mls_number && <Chip label={`MLS# ${stop.property.mls_number}`} size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }} />}
                            </Box>
                          }
                          secondary={`Arrive ${fmt(stop.arrival_time)} · ${stop.duration_minutes} min · Leave ${fmt(stop.departure_time)}`}
                        />
                        <Tooltip title="Go here (from current location)">
                          <IconButton size="small" onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${stop.property.latitude},${stop.property.longitude}&travelmode=driving`, '_blank')}>
                            <NearMeIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={`Start full route from stop ${i + 1}`}>
                          <IconButton size="small" onClick={() => window.open(buildMapsUrl(i), '_blank')} sx={{ color: 'text.secondary' }}>
                            <MapIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </ListItem>
                    </React.Fragment>
                  ))}

                  {/* Return leg */}
                  <ListItem disablePadding sx={{ pl: '20px', py: 0 }}>
                    <Typography variant="caption" color="text.secondary">
                      ↓ {result.return_leg.travel_time_minutes} min · {result.return_leg.distance_km} km (return)
                    </Typography>
                  </ListItem>
                  <ListItem disablePadding sx={{ mb: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Box sx={{ width: 28, height: 28, borderRadius: '50%', background: '#2e7d32', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>🏠</Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={result.start_location.address}
                      secondary={`Return by ${fmt(result.return_leg.arrival_time)}`}
                    />
                  </ListItem>
                </List>
              </Paper>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default DayPlanner;
